<template>
  <b-overlay class="loader" :no-wrap="true" :no-center="true" :show="isShow" fixed>
    <template #overlay>
      <div class="loader">
        <div class="wrap-custom-progress">
          <h1 class="mb-3">Optimization & Creating Report</h1>
          <b-progress height="45px" :max="max">
            <span class="progress-value">{{ progress }} %</span>
            <b-progress-bar :value="progress" />
          </b-progress>
          <p class="mt-1 text-xxs" v-text="message ? message : 'Waiting for the server to start processing.'" />
          <p class="text-center mt-4">This can take up to several minutes, depending on how busy the server is.</p>
          <p class="overlay-bottom-text">
            You don't have to wait on this page while it loads, feel free to go to
            <router-link :to="{ name: assignmentsRoute }" @click="closeLoader">Assignments List</router-link>
          </p>
        </div>
      </div>
    </template>
  </b-overlay>
</template>

<script lang="ts">
import type { Loader } from '@/types/Models/Loader'
import type { UILoader } from '@/types/Store'
import { defineComponent, computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { OPTIMISING, REPORT_ACCEPTED, REPORT_RELEASED } from '@/const/AssignmentStatuses'
import { OPTIMIZE_CREATING_REPORT_LOADER } from '@/const/LoadersNames'
import { checkMessage } from '@/services/WebSockets'
import { useAccountStore } from '@/store/account'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { useUiStore } from '@/store/ui'
import {
  CUSTOMER_ASSIGNMENTS_OPTIMIZATION_REPORT,
  CUSTOMER_ASSIGNMENTS_DETAILS,
  STAFF_ASSIGNMENTS_OPTIMIZATION_REPORT,
  STAFF_ASSIGNMENTS_DETAILS,
  STAFF_ASSIGNMENTS_LIST,
  CUSTOMER_ASSIGNMENTS_LIST,
} from '@/types/router'

export default defineComponent({
  name: 'OptimizationCreatingReportLoader',
  setup() {
    const assignmentSummaryStore = useAssignmentSummaryStore()
    const accountStore = useAccountStore()
    const uiStore = useUiStore()
    const router = useRouter()
    const max: Ref<number> = ref(100)
    const progress: Ref<number> = ref(0)
    const message: Ref<string | null> = ref(null)
    const status: Ref<string | null> = ref(null)
    const loader: ComputedRef<UILoader | null> = computed(() => uiStore.loader ?? null)
    const assignmentId: ComputedRef<string> = computed(() => assignmentSummaryStore.id ?? '')
    const assignmentRef: ComputedRef<string> = computed(() => assignmentSummaryStore.reference ?? '')
    const userId: ComputedRef<string | null> = computed(() => accountStore.userId)
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const isShow: ComputedRef<boolean> = computed(
      () => loader.value?.name === OPTIMIZE_CREATING_REPORT_LOADER && Boolean(loader.value?.show),
    )

    const assignmentsRoute: ComputedRef<string> = computed(() =>
      isStaff.value ? STAFF_ASSIGNMENTS_LIST : CUSTOMER_ASSIGNMENTS_LIST,
    )

    const closeLoader = () => {
      uiStore.loaderHide({ name: OPTIMIZE_CREATING_REPORT_LOADER })
    }

    const parseLoaderProgress = (response: Loader) => {
      progress.value = progress.value === 100 ? 100 : response.progress
      message.value = typeof response.message !== 'undefined' ? response.message : null
    }

    watch(status, (newVal, oldVal) => {
      if (!isShow.value) {
        return
      }

      if (oldVal !== OPTIMISING) {
        return
      }

      let next: string = ''
      if (newVal === REPORT_RELEASED) {
        next = isStaff.value ? STAFF_ASSIGNMENTS_OPTIMIZATION_REPORT : CUSTOMER_ASSIGNMENTS_OPTIMIZATION_REPORT
      } else if (newVal === REPORT_ACCEPTED) {
        next = isStaff.value ? STAFF_ASSIGNMENTS_DETAILS : CUSTOMER_ASSIGNMENTS_DETAILS
      }

      if (!next) {
        return
      }

      progress.value = 100
      setTimeout(() => {
        router.push({
          name: next,
          params: { id: assignmentRef.value },
        })
        uiStore.loaderHide()
      }, 1000)
    })

    watch(isShow, (val: boolean) => {
      progress.value = 0
      if (val) {
        status.value = assignmentSummaryStore.assignmentStatus
        checkMessage(message, progress)
        window.Echo.private(`App.User.${userId.value}.Assignment.${assignmentId.value}`).listen(
          '.optimisation-progress',
          (response: Loader) => parseLoaderProgress(response),
        )
        window.Echo.channel('App.Assignments').listen(
          '\\App\\Events\\Assignment\\AssignmentStatusChanged',
          (data: any) => {
            if (data.assignment?.id === assignmentId.value) {
              status.value = data.assignment.status
            }
          },
        )
      }
    })

    return {
      max,
      isShow,
      message,
      progress,
      assignmentsRoute,
      closeLoader,
    }
  },
})
</script>
